import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  useReimbursementVerificationPPU,
  useReimbursementVerificationPPUPetCare,
  useReimbursementVerificationSpotCanada,
  useSMSVerificationPPU,
  useSMSVerificationPPUPetCare,
  useSMSVerificationSpotCanada,
  useTimer,
} from '../../../../hooks';
import { MARKETING_CHANNEL_ID } from '../../../../theme/theme.constants';
import {
  sendVerificationCode,
  verifyCode,
  verifyCodeReset,
} from '../../../../actions';
import { OTP_VERIFICATION_TYPE } from '../../../common/otp/otp.constants';
import { getServiceNameByMarketChannelId } from '../../../common/otp/otp.utils';

const useReimbursementValidations = () => {
  const store = useSelector(({
    otpVerification,
    personalInformation,
  }) => ({ otpVerification, personalInformation }), shallowEqual);
  const {
    personalInformation: { userInfo },
    otpVerification: { verificationCode: verificationState },
  } = store;
  const { t } = useTranslation('insurance');
  const dispatch = useDispatch();
  const reimbursementVerificationPPU = useReimbursementVerificationPPU();
  const smsVerificationPPU = useSMSVerificationPPU();

  const reimbursementVerificationPPUPetCare =
    useReimbursementVerificationPPUPetCare();
  const smsVerificationPPUPetCare = useSMSVerificationPPUPetCare();

  const reimbursementVerificationSpotCanada =
    useReimbursementVerificationSpotCanada();
  const smsVerificationSpotCanada = useSMSVerificationSpotCanada();
  const { remainingSeconds, startTimer } = useTimer(5);

  const shouldVerifyReimbursement = useCallback((
    marketChannelId = 0,
  ) => {
    if (!marketChannelId) {
      return false;
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.ppu) {
      return reimbursementVerificationPPU;
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.ppuPetCare) {
      return reimbursementVerificationPPUPetCare;
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.spotCan) {
      return reimbursementVerificationSpotCanada;
    }

    // WIP: add the rest of canada brands

    return false;
  }, [
    reimbursementVerificationPPU,
    reimbursementVerificationPPUPetCare,
    reimbursementVerificationSpotCanada,
  ]);

  const getContactMethod = useCallback((marketChannelId = 0) => {
    if (!marketChannelId) {
      return '';
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.ppu && smsVerificationPPU) {
      return t('verifyAccountModal.contactMethodSMS');
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.ppuPetCare
      && smsVerificationPPUPetCare) {
      return t('verifyAccountModal.contactMethodSMS');
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.spotCan
      && smsVerificationSpotCanada) {
      return t('verifyAccountModal.contactMethodSMS');
    }

    return t('verifyAccountModal.contactMethodEmail');
  }, [
    smsVerificationPPU,
    smsVerificationPPUPetCare,
    smsVerificationSpotCanada,
    t,
  ]);

  const userData = useMemo(() => {
    if (!userInfo) {
      return { email: '', phoneNumber: '' };
    }

    return {
      email: userInfo.LoginEmail,
      phoneNumber: userInfo.PhoneNumber,
    };
  }, [userInfo]);

  const missingContactInformation = useCallback((marketChannelId) => {
    if (!marketChannelId) {
      return false;
    }

    const smsVerificationByMarketChannel = {
      [MARKETING_CHANNEL_ID.ppu]: smsVerificationPPU,
      [MARKETING_CHANNEL_ID.ppuPetCare]: smsVerificationPPUPetCare,
      [MARKETING_CHANNEL_ID.spotCan]: smsVerificationSpotCanada,
    };

    const smsVerificationFlag = smsVerificationByMarketChannel[marketChannelId];

    return smsVerificationFlag ? !userData.phoneNumber : !userData.email;
  }, [
    smsVerificationPPU,
    smsVerificationPPUPetCare,
    smsVerificationSpotCanada,
    userData,
  ]);

  const getMaskedContact = useCallback((marketChannelId = 0) => {
    if (!marketChannelId) {
      return '';
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.ppu && smsVerificationPPU) {
      return userData.phoneNumber;
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.ppuPetCare
      && smsVerificationPPUPetCare) {
      return userData.phoneNumber;
    }

    if (marketChannelId === MARKETING_CHANNEL_ID.spotCan
      && smsVerificationSpotCanada) {
      return userData.phoneNumber;
    }

    return userData.email;
  }, [
    smsVerificationPPU,
    smsVerificationPPUPetCare,
    smsVerificationSpotCanada,
    t,
    userData,
  ]);

  const getVerificationTypeByMarketChannel = useCallback((
    marketChannelId = 0,
  ) => {
    let verificationType = '';

    if (marketChannelId === MARKETING_CHANNEL_ID.ppu) {
      verificationType = smsVerificationPPU
        ? OTP_VERIFICATION_TYPE.SMS : OTP_VERIFICATION_TYPE.EMAIL;
    } else if (marketChannelId === MARKETING_CHANNEL_ID.ppuPetCare) {
      verificationType = smsVerificationPPUPetCare
        ? OTP_VERIFICATION_TYPE.SMS : OTP_VERIFICATION_TYPE.EMAIL;
    } else if (marketChannelId === MARKETING_CHANNEL_ID.spotCan) {
      verificationType = smsVerificationSpotCanada
        ? OTP_VERIFICATION_TYPE.SMS : OTP_VERIFICATION_TYPE.EMAIL;
    }

    return verificationType;
  }, [
    smsVerificationPPU,
    smsVerificationPPUPetCare,
    smsVerificationSpotCanada,
  ]);

  const doSendVerificationCode = useCallback((marketChannelId = 0) => () => {
    startTimer();

    dispatch(sendVerificationCode({
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      serviceName: getServiceNameByMarketChannelId(marketChannelId),
      verificationType: getVerificationTypeByMarketChannel(marketChannelId),
    }));
  }, [
    dispatch,
    getVerificationTypeByMarketChannel,
    startTimer,
    userData,
  ]);

  const doVerifyCode = useCallback((marketChannelId) => (code) => {
    dispatch(verifyCodeReset());

    dispatch(verifyCode({
      code,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      verificationType: getVerificationTypeByMarketChannel(marketChannelId),
    }));
  }, [
    dispatch,
    getVerificationTypeByMarketChannel,
    userData,
  ]);

  useEffect(() => () => dispatch(verifyCodeReset()), []);

  return {
    doSendVerificationCode,
    doVerifyCode,
    errorMessage: verificationState.error,
    getContactMethod,
    getMaskedContact,
    loading: verificationState.isLoading,
    missingContactInformation,
    remainingSeconds,
    shouldVerifyReimbursement,
    verified: verificationState.verified,
  };
};

export { useReimbursementValidations };
